class TfBuyActions extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onPropertyChange = this.onPropertyChange.bind(this);
  }

  connectedCallback() {
    /* ================= CONFIG ================= */
    this.buyNowUrl = this.getAttribute('data-buy-now') || '/checkout';
    this.moneyFormat = this.getAttribute('data-money-format') || null;
    this.initialUnitPrice = parseInt(this.getAttribute('data-initial-price') || '0', 10) || 0;
    this.cartDrawerSelector = this.getAttribute('data-cart-drawer') || 'cart-drawer';

    /* ================= ROOT ================= */
    this.root =
      this.closest('[data-product-root]') ||
      this.closest('.shopify-section') ||
      document;

    /* ================= ELEMENTS ================= */
    this.btnAdd = this.querySelector('[data-action="add-to-bag"]');
    this.btnAddText = this.btnAdd?.querySelector('.text');
    this.addText = this.btnAddText?.textContent.trim() || this.btnAdd?.textContent.trim() || '';
    this.soldoutText = this.btnAdd?.getAttribute('data-soldout') || 'Sold out';

    this.btnBuy = this.querySelector('[data-action="buy-now"]');

    this.priceWrap = this.querySelector('[data-product-price]') || this;
    this.priceEl = this.priceWrap.querySelector('[data-price-new]');

    /* ================= STATE ================= */
    this.variantId = this.getVariantId();
    this.qty = this.getQty();
    this.unitPrice = this.getUnitPrice();
    this.isAvailable = true; // will be set on variant:change

    /* ================= EVENTS ================= */
    this.root.addEventListener('variant:change', this.onVariantChange);
    this.root.addEventListener('quantity:change', this.onQuantityChange);
    this.root.addEventListener('change', this.onPropertyChange);

    const qtyInput = this.getQtyInput();
    if (qtyInput) {
      qtyInput.addEventListener('input', this.onQuantityChange);
      qtyInput.addEventListener('change', this.onQuantityChange);
    }

    this.addEventListener('click', this.onClick);

    /* ================= INIT ================= */
    this.syncPrice();

    // init availability from current variant if possible
    const v = this.getVariantFromPicker(this.variantId);
    if (v) this.setAvailability(!!v.available);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.root.removeEventListener('variant:change', this.onVariantChange);
    this.root.removeEventListener('quantity:change', this.onQuantityChange);
    this.root.removeEventListener('change', this.onPropertyChange);
  }

  /* ======================================================
    HELPERS
  ====================================================== */

  setLoading(state) {
    this.classList.toggle('is-loading', state);
    if (this.btnAdd) this.btnAdd.setAttribute('aria-busy', state ? 'true' : 'false');
  }

setAvailability(available) {
  this.isAvailable = !!available;

  const toggleBtn = (btn, disabled) => {
    if (!btn) return;
    btn.classList.toggle('disabled', disabled);
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');

    if (disabled) {
      btn.style.pointerEvents = 'none';
      btn.tabIndex = -1;
    } else {
      btn.style.pointerEvents = '';
      btn.removeAttribute('tabindex');
    }
  };

  const disabled = !this.isAvailable;
  toggleBtn(this.btnAdd, disabled);
  toggleBtn(this.btnBuy, disabled);

  if (this.btnAddText) {
    this.btnAddText.textContent = disabled ? this.soldoutText : this.addText;
  }
}

  formatMoney(cents) {
    const fmt = this.getAttribute('data-money-format') || '{{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }

    const value = Number(cents) || 0;
    const formatWithDelimiters = (amountCents, precision, thousands, decimal) => {
      const number = (amountCents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    };

    let token = 'amount';
    const m = fmt.match(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/);
    if (m && m[1]) token = m[1];

    let formattedNumber = '';
    switch (token) {
      case 'amount':
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formattedNumber = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
    }

    if (!m) return formattedNumber;
    return fmt.replace(m[0], formattedNumber);
  }

  getVariantId() {
    const el = this.root.querySelector('input[name="id"], [data-variant-id]');
    const v = parseInt(el?.value || el?.getAttribute('value'), 10);
    return Number.isFinite(v) ? v : null;
  }

  getQtyInput() {
    return this.root.querySelector('input[name="quantity"], [data-quantity-input]');
  }

  getQty() {
    const el = this.getQtyInput();
    const v = parseInt(el?.value || '1', 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  getLineItemProperties() {
    const properties = {};
    this.root.querySelectorAll('[data-line-item-property]').forEach((field) => {
      const match = String(field.name || '').match(/^properties\[(.+)\]$/);
      const value = String(field.value || '').trim();
      if (match && value) properties[match[1]] = value;
    });
    return properties;
  }

  validateLineItemProperties() {
    let firstInvalid = null;

    this.root.querySelectorAll('[data-line-item-property][required]').forEach((field) => {
      const invalid = !String(field.value || '').trim();
      const wrapper = field.closest('[data-ring-size-selector]');
      const error = wrapper?.querySelector('[data-property-error]');

      field.classList.toggle('is-invalid', invalid);
      field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (error) error.hidden = !invalid;
      if (invalid && !firstInvalid) firstInvalid = field;
    });

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  }

  getVariantsJSON() {
    const picker = this.root.querySelector('tf-variant-picker');
    const jsonEl = picker?.querySelector('script[data-variants]');
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || '[]');
    } catch {
      return null;
    }
  }

  getVariantFromPicker(variantId) {
    if (!variantId) return null;
    const variants = this.getVariantsJSON();
    if (!Array.isArray(variants)) return null;
    return variants.find(x => Number(x.id) === Number(variantId)) || null;
  }

  getUnitPrice() {
    const v = this.getVariantFromPicker(this.variantId);
    if (v && typeof v.price !== 'undefined') return Number(v.price) || this.initialUnitPrice;
    return this.initialUnitPrice;
  }

  syncPrice() {
    if (!this.priceEl) return;
    const total = (this.unitPrice || 0) * (this.qty || 1);
    this.priceEl.textContent = this.formatMoney(total);
  }

  /* ======================================================
    EVENTS
  ====================================================== */

  onVariantChange(e) {
    const v = e.detail?.variant;
    if (!v) return;

    this.variantId = Number(v.id);
    this.unitPrice = Number(v.price) || this.unitPrice;

    const idEl = this.root.querySelector('input[name="id"], [data-variant-id]');
    if (idEl) idEl.value = this.variantId;

    // ✅ disable buttons on sold out
    this.setAvailability(!!v.available);

    this.syncPrice();
  }

  onQuantityChange() {
    this.qty = this.getQty();
    this.syncPrice();
  }

  onPropertyChange(e) {
    const field = e.target.closest?.('[data-line-item-property]');
    if (!field || !String(field.value || '').trim()) return;

    field.classList.remove('is-invalid');
    field.setAttribute('aria-invalid', 'false');
    const error = field.closest('[data-ring-size-selector]')?.querySelector('[data-property-error]');
    if (error) error.hidden = true;
  }

  /* ======================================================
    CART DRAWER
  ====================================================== */

  getCartDrawer() {
    return document.querySelector(this.cartDrawerSelector);
  }

  async updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const bubble = document.querySelector('.cart-count-bubble span');
      if (bubble) bubble.textContent = cart.item_count;
    } catch {}
  }

  async refreshAndOpenDrawer() {
    await customElements.whenDefined('cart-drawer');

    const drawer = this.getCartDrawer();
    if (!drawer) return;

    const CART_URL = window.routes?.cart_url || '/cart';
    const res = await fetch(`${CART_URL}?section_id=cart-drawer`);
    if (!res.ok) return;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');

    if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;

    requestAnimationFrame(() => {
      if (typeof drawer.open === 'function') {
        drawer.open(this.btnAdd);
      } else {
        document.querySelector('#cart-icon-bubble')?.click?.();
      }
    });
  }

  /* ======================================================
    ADD TO CART
  ====================================================== */

  async addToCart({ goCheckout = false } = {}) {
    // ✅ stop if sold out/disabled
    if (!this.isAvailable) return;

    this.variantId = this.getVariantId();
    this.qty = this.getQty();

    if (!this.variantId) return;
    if (!this.validateLineItemProperties()) return;

    const properties = this.getLineItemProperties();

    this.setLoading(true);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.variantId, quantity: this.qty, properties })
      });

      if (!res.ok) throw new Error(await res.text());

      this.dispatchEvent(new CustomEvent('cart:added', {
        bubbles: true,
        detail: { id: this.variantId, quantity: this.qty, goCheckout }
      }));

      if (goCheckout) {
        window.location.href = this.buyNowUrl;
        return;
      }

      await this.updateCartCount();
      await this.refreshAndOpenDrawer();
    } catch (e) {
      console.error('[tf-buy-actions] addToCart error:', e);
    } finally {
      this.setLoading(false);
    }
  }

  /* ======================================================
    CLICK
  ====================================================== */

  onClick(e) {
    const add = e.target.closest('[data-action="add-to-bag"]');
    const buy = e.target.closest('[data-action="buy-now"]');

    // ✅ block clicks when disabled
    if (add && (add.classList.contains('disabled') || add.getAttribute('aria-disabled') === 'true')) {
      e.preventDefault();
      return;
    }
    if (buy && (buy.classList.contains('disabled') || buy.getAttribute('aria-disabled') === 'true')) {
      e.preventDefault();
      return;
    }

    if (add && this.contains(add)) {
      e.preventDefault();
      this.addToCart({ goCheckout: false });
      return;
    }

    if (buy && this.contains(buy)) {
      e.preventDefault();
      this.addToCart({ goCheckout: true });
    }
  }
}

if (!customElements.get('tf-buy-actions')) {
  customElements.define('tf-buy-actions', TfBuyActions);
}
